import React, { useEffect, useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Database, FileText, Upload, Info, ArrowRight, AlertTriangle, XCircle, Loader2, ChevronRight, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/SubscriptionAppButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { Input } from '../components/SubscriptionAppInput';
import { Card } from '../components/SubscriptionAppCard';
// --- Types ---
type Step = 1 | 2 | 3 | 4;
type Platform = 'Recharge' | 'WineHub' | 'Commerce7' | 'WineDirect' | 'Shopify Native' | 'CSV Upload';
interface FieldMapping {
  source: string;
  target: string;
  status: 'mapped' | 'unmapped';
  skipped: boolean;
}
interface PreviewRow {
  id: number;
  email: string;
  name: string;
  plan: string;
  status: 'Active' | 'Paused' | 'Cancelled';
  joinDate: string;
  nextBilling: string;
  validation: 'valid' | 'warning' | 'error';
  message?: string;
}
// --- Mock Data ---
const PLATFORMS: {
  id: Platform;
  description: string;
  icon: React.ElementType;
}[] = [{
  id: 'Recharge',
  description: 'Shopify subscription app',
  icon: Database
}, {
  id: 'WineHub',
  description: 'Wine club management platform',
  icon: Database
}, {
  id: 'Commerce7',
  description: 'Wine DTC platform',
  icon: Database
}, {
  id: 'WineDirect',
  description: 'Wine eCommerce platform',
  icon: Database
}, {
  id: 'Shopify Native',
  description: 'Shopify built-in subscriptions',
  icon: Database
}, {
  id: 'CSV Upload',
  description: 'Upload a spreadsheet',
  icon: FileText
}];
const MOCK_MAPPINGS: FieldMapping[] = [{
  source: 'customer_email',
  target: 'Email',
  status: 'mapped',
  skipped: false
}, {
  source: 'first_name',
  target: 'First Name',
  status: 'mapped',
  skipped: false
}, {
  source: 'last_name',
  target: 'Last Name',
  status: 'mapped',
  skipped: false
}, {
  source: 'created_at',
  target: 'Join Date',
  status: 'mapped',
  skipped: false
}, {
  source: 'subscription_plan',
  target: 'Subscription Plan',
  status: 'mapped',
  skipped: false
}, {
  source: 'status',
  target: 'Status',
  status: 'mapped',
  skipped: false
}, {
  source: 'next_billing',
  target: 'Next Billing Date',
  status: 'mapped',
  skipped: false
}, {
  source: 'price',
  target: 'Price',
  status: 'mapped',
  skipped: false
}, {
  source: 'phone',
  target: 'Phone',
  status: 'mapped',
  skipped: false
}, {
  source: 'loyalty_points',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'custom_field_1',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'referral_code',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'internal_notes',
  target: '',
  status: 'unmapped',
  skipped: false
}, {
  source: 'legacy_id',
  target: '',
  status: 'unmapped',
  skipped: false
}];
const TARGET_FIELDS = ['Email', 'First Name', 'Last Name', 'Phone', 'Subscription Plan', 'Status', 'Join Date', 'Next Billing Date', 'Billing Interval', 'Price', 'Payment Method', 'Shipping Address', 'Tags', 'Notes'];
const PREVIEW_DATA: PreviewRow[] = [{
  id: 1,
  email: 'sarah.j@example.com',
  name: 'Sarah Johnson',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-01-15',
  nextBilling: '2026-02-15',
  validation: 'valid'
}, {
  id: 2,
  email: 'mike.smith@test.com',
  name: 'Mike Smith',
  plan: 'Red Only',
  status: 'Active',
  joinDate: '2022-11-01',
  nextBilling: '2026-02-01',
  validation: 'warning',
  message: 'Email already exists in WinePik — will merge'
}, {
  id: 3,
  email: '',
  name: 'Unknown User',
  plan: 'Club 6',
  status: 'Paused',
  joinDate: '2024-03-10',
  nextBilling: '2026-03-10',
  validation: 'error',
  message: 'Missing required field: Email'
}, {
  id: 4,
  email: 'emma.w@winery.com',
  name: 'Emma Williams',
  plan: 'Reserve Club',
  status: 'Active',
  joinDate: '2023-05-20',
  nextBilling: '2026-02-20',
  validation: 'valid'
}, {
  id: 5,
  email: 'david.b@example.org',
  name: 'David Brown',
  plan: 'White Only',
  status: 'Cancelled',
  joinDate: '2021-08-15',
  nextBilling: '-',
  validation: 'valid'
}];
export function MigrationWizardPage() {
  const navigate = useNavigate();
  // --- State ---
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // Mock file
  const [migrationName, setMigrationName] = useState('');
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>(MOCK_MAPPINGS);
  const [previewFilter, setPreviewFilter] = useState<'All' | 'Valid' | 'Warnings' | 'Errors'>('All');
  const [duplicateHandling, setDuplicateHandling] = useState<'skip' | 'merge' | 'overwrite'>('skip');
  const [testImport, setTestImport] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importLogs, setImportLogs] = useState<string[]>([]);
  const [isPolling, setIsPolling] = useState(false);
  // --- Handlers ---
  const handleDone = () => {
    setIsPolling(true);
    setTimeout(() => {
      setIsPolling(false);
      navigate('/management/migration');
    }, 2500);
  };
  const handlePlatformSelect = (platform: Platform) => {
    setSelectedPlatform(platform);
    const date = new Date().toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    setMigrationName(`${platform} Subscriber Import — ${date}`);
  };
  const handleFileUpload = () => {
    // Simulate file upload
    const mockFile = new File([''], 'recharge_export_jan2026.csv', {
      type: 'text/csv'
    });
    setUploadedFile(mockFile);
  };
  const handleRemoveFile = () => {
    setUploadedFile(null);
  };
  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1 as Step);
    }
  };
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1 as Step);
    }
  };
  const handleStartImport = () => {
    setIsImporting(true);
    setImportProgress(0);
    setImportLogs([]);
    // Simulate import process
    let progress = 0;
    const interval = setInterval(() => {
      progress += Math.random() * 5;
      if (progress > 100) progress = 100;
      setImportProgress(Math.round(progress));
      // Add mock logs
      if (progress > 10 && importLogs.length === 0) {
        setImportLogs(prev => [...prev, 'sarah.j@example.com — imported']);
      }
      if (progress > 30 && importLogs.length === 1) {
        setImportLogs(prev => [...prev, 'emma.w@winery.com — imported']);
      }
      if (progress > 60 && importLogs.length === 2) {
        setImportLogs(prev => [...prev, 'duplicate skipped: mike.smith@test.com']);
      }
      if (progress > 90 && importLogs.length === 3) {
        setImportLogs(prev => [...prev, 'david.b@example.org — imported']);
      }
      if (progress === 100) {
        clearInterval(interval);
        setTimeout(() => {
          setIsImporting(false);
        }, 1000);
      }
    }, 200);
  };
  // --- Render Helpers ---
  const renderStepper = () => {
    const steps = [{
      num: 1,
      label: 'Source'
    }, {
      num: 2,
      label: 'Map Fields'
    }, {
      num: 3,
      label: 'Preview & Configure'
    }, {
      num: 4,
      label: 'Results'
    }];
    return <div className="flex items-center justify-center max-w-2xl mx-auto" data-id="element-11114" data-mp="2kdwcce5e0m0x">
        {steps.map((step, index) => {
        const isActive = currentStep === step.num;
        const isCompleted = currentStep > step.num;
        const isFuture = currentStep < step.num;
        return <Fragment key={step.num} data-id="element-11115" data-mp="2gazpuccq85rp" data-mpi={index}>
              {/* Step node */}
              <div className="flex flex-col items-center relative z-10" data-id="element-11116" data-mp="2gdcef8czpedq">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-[13px] font-semibold transition-all duration-200 ${isCompleted ? 'bg-[#10B981] text-white shadow-[0_0_0_3px_rgba(16,185,129,0.15)]' : isActive ? 'bg-[#0EA5E9] text-white shadow-[0_0_0_4px_rgba(14,165,233,0.15)]' : 'bg-[#F1F5F9] text-[#94A3B8] border border-[#E2E8F0]'}`} data-id="element-11117" data-mp="zvewjf3az386">
                  {isCompleted ? <Check className="w-4 h-4" strokeWidth={2.5} data-id="element-11118" data-mp="1mmj51wzexlmq" /> : step.num}
                </div>
                <span className={`mt-2 text-[12px] whitespace-nowrap ${isActive ? 'font-semibold text-[#0F172A]' : isCompleted ? 'font-medium text-[#475569]' : 'font-normal text-[#94A3B8]'}`} data-id="element-11119" data-mp="1hnexfas4zvyi">
                  {step.num}. {step.label}
                </span>
              </div>

              {/* Connecting line between steps */}
              {index < steps.length - 1 && <div className="flex-1 mx-3 mt-[-18px]" data-id="element-11120" data-mp="20l92xeu23pc5">
                  <div className={`h-[2px] w-full rounded-full transition-colors duration-200 ${currentStep > step.num + 1 ? 'bg-[#10B981]' : currentStep > step.num ? 'bg-[#0EA5E9]' : 'bg-[#E2E8F0]'}`} data-id="element-11121" data-mp="20kpucoqaqdas" />
                </div>}
            </Fragment>;
      })}
      </div>;
  };
  // --- Step Content Renderers ---
  const renderStep1 = () => <div className="max-w-4xl mx-auto space-y-8" data-id="element-11122" data-mp="30nfj3yvkmo26">
      <div className="text-center" data-id="element-11123" data-mp="35l7amrsb085u">
        <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-11124" data-mp="35m8low9bkunk">
          Where are you migrating from?
        </h2>
        <p className="text-[#64748B] mt-2" data-id="element-11125" data-mp="3allio9twni3v">
          Select your source platform or upload a CSV file.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11126" data-mp="3jkgq1p8oqy8r">
        {PLATFORMS.map(platform => {
        const isSelected = selectedPlatform === platform.id;
        const Icon = platform.icon;
        return <div key={platform.id} onClick={() => handlePlatformSelect(platform.id)} className={`relative p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${isSelected ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-11127" data-mp="3t972jv1cq4m0">
              {isSelected && <div className="absolute top-3 right-3 text-[#0EA5E9]" data-id="element-11128" data-mp="b4wxytjr3lva">
                  <CheckCircle className="w-5 h-5" data-id="element-11129" data-mp="2ge3x9eb97wbd" />
                </div>}
              <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex items-center justify-center mb-3" data-id="element-11130" data-mp="2lcf8nth76evp">
                <Icon className="w-5 h-5 text-[#64748B]" data-id="element-11131" data-mp="2qbs595xvf0bh" />
              </div>
              <h3 className="font-semibold text-[#0F172A]" data-id="element-11132" data-mp="2q9p3xpr9pkrg">{platform.id}</h3>
              <p className="text-xs text-[#64748B] mt-1" data-id="element-11133" data-mp="2v3tf6l7kgeyc">
                {platform.description}
              </p>
            </div>;
      })}
      </div>

      {selectedPlatform && <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-11134" data-mp="341vl9f65pxsh">
          {selectedPlatform !== 'CSV Upload' && <div className="bg-[#E0F2FE] border border-[#7DD3FC] rounded-lg p-4 flex items-start gap-3" data-id="element-11135" data-mp="392szxhryk9p8">
              <Info className="w-5 h-5 text-[#0284C7] flex-shrink-0 mt-0.5" data-id="element-11136" data-mp="3dz09dkjkc3bx" />
              <div className="text-sm text-[#0369A1]" data-id="element-11137" data-mp="1epa54fpbb57m">
                <p data-id="element-11138" data-mp="1jmtjdfm0fhzk">
                  Export your subscriber data from{' '}
                  <strong data-id="element-11139" data-mp="1jowkauopavj2">{selectedPlatform}</strong> as a CSV file.
                </p>
                <button className="flex items-center gap-1 font-medium mt-1 hover:underline" data-id="element-11140" data-mp="1c5nbb2k3wyuj">
                  View our export guide for {selectedPlatform}{' '}
                  <ArrowRight className="w-3 h-3" data-id="element-11141" data-mp="12eso9vz1dv81" />
                </button>
              </div>
            </div>}

          {!uploadedFile ? <div onClick={handleFileUpload} className="border-2 border-dashed border-[#CBD5E1] rounded-lg p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:border-[#0EA5E9] hover:bg-[#F8FAFC] transition-colors" data-id="element-11142" data-mp="xghefl8qrkpt">
              <div className="w-12 h-12 rounded-full bg-[#F1F5F9] flex items-center justify-center mb-4" data-id="element-11143" data-mp="1l3z2p6sss0o1">
                <Upload className="w-6 h-6 text-[#64748B]" data-id="element-11144" data-mp="1g5nqipf15e2n" />
              </div>
              <h4 className="text-[#0F172A] font-medium mb-1" data-id="element-11145" data-mp="9p25h9mgm55l">
                Drag and drop your CSV file here
              </h4>
              <p className="text-sm text-[#64748B]" data-id="element-11146" data-mp="4oxq5mxafggy">
                or click to browse. Supports .csv and .xlsx files up to 10MB.
              </p>
            </div> : <Card padding="sm" className="flex items-center justify-between" data-id="element-11147" data-mp="2su3d0fj69q0q">
              <div className="flex items-center gap-4" data-id="element-11148" data-mp="2wwq5mino3eyz">
                <div className="w-10 h-10 rounded-lg bg-[#F1F5F9] flex items-center justify-center" data-id="element-11149" data-mp="2iyjf5nl1sh9c">
                  <FileText className="w-5 h-5 text-[#64748B]" data-id="element-11150" data-mp="2nw1pniepr4jg" />
                </div>
                <div data-id="element-11151" data-mp="297f1cr73z2g3">
                  <p className="font-medium text-[#0F172A]" data-id="element-11152" data-mp="296vt6279fsf9">
                    {uploadedFile.name}
                  </p>
                  <p className="text-xs text-[#64748B]" data-id="element-11153" data-mp="2dzyfbp9pjzlf">
                    2.4 MB • 847 rows, 14 columns detected
                  </p>
                </div>
              </div>
              <button onClick={handleRemoveFile} className="text-sm font-medium text-[#EF4444] hover:text-[#DC2626]" data-id="element-11154" data-mp="2574wxiyysk3c">
                Remove
              </button>
            </Card>}

          <Input label="Migration Name" placeholder="e.g. Recharge Subscriber Import — January 2026" value={migrationName} onChange={e => setMigrationName(e.target.value)} data-id="element-11155" data-mp="1v8fftsew73o5" />
        </div>}
    </div>;
  const renderStep2 = () => <div className="max-w-5xl mx-auto space-y-6" data-id="element-11156" data-mp="17m3nht5z8t0b">
      <div className="flex items-center justify-between" data-id="element-11157" data-mp="1lkjs9eka49ft">
        <div data-id="element-11158" data-mp="1lm4n75ai9ue7">
          <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-11159" data-mp="1ligwsf0cxo3p">
            Map your columns
          </h2>
          <p className="text-[#64748B] mt-1" data-id="element-11160" data-mp="1gntgh3fic007">
            Match your source columns to WinePik fields.
          </p>
        </div>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg px-4 py-3 flex items-center gap-2 text-sm" data-id="element-11161" data-mp="1qkg0jq00en16">
        <Info className="w-4 h-4 text-[#64748B]" data-id="element-11162" data-mp="249zy8heqgu3m" />
        <span className="text-[#0F172A]" data-id="element-11163" data-mp="24cvv02euhcqh">
          14 source columns detected. 9 auto-mapped.
        </span>
        <span className="font-medium text-[#F59E0B]" data-id="element-11164" data-mp="2e98qn5tya0nv">5 need review</span>
      </div>

      <Card padding="none" className="divide-y divide-[#E2E8F0]" data-id="element-11165" data-mp="2e6usup7vyacm">
        {fieldMappings.map((mapping, idx) => <div key={idx} className="flex items-center gap-4 p-4 hover:bg-[#F8FAFC] transition-colors" data-id="element-11166" data-mp="298b9mwkofcny" data-mpi={idx}>
            <div className="w-1/3 flex items-center justify-end gap-3" data-id="element-11167" data-mp="bmk31l497zcr">
              <span className="font-mono text-sm bg-[#F1F5F9] px-2 py-1 rounded text-[#475569]" data-id="element-11168" data-mp="bkqsvvufmx00">
                {mapping.source}
              </span>
            </div>

            <div className="flex-shrink-0 text-[#94A3B8]" data-id="element-11169" data-mp="lbbuls5ekhn5">
              <ArrowRight className="w-4 h-4" data-id="element-11170" data-mp="3ju0n01mbvnkr" />
            </div>

            <div className="w-1/3" data-id="element-11171" data-mp="3jvtrba9gxvpl">
              <div className="relative" data-id="element-11172" data-mp="3ostwzk2eowg6">
                <select className={`w-full h-10 pl-3 pr-8 border rounded-md text-sm appearance-none bg-white focus:outline-none focus:ring-2 ${mapping.status === 'mapped' ? 'border-[#CBD5E1] text-[#0F172A] focus:border-[#0EA5E9] focus:ring-[#0EA5E9]/20' : 'border-[#F59E0B] text-[#0F172A] focus:border-[#F59E0B] focus:ring-[#F59E0B]/20'}`} value={mapping.target} onChange={e => {
              const newMappings = [...fieldMappings];
              newMappings[idx].target = e.target.value;
              newMappings[idx].status = e.target.value ? 'mapped' : 'unmapped';
              setFieldMappings(newMappings);
            }} disabled={mapping.skipped} data-id="element-11173" data-mp="3otvbjyims12n">
                  <option value="" data-id="element-11174" data-mp="2ot09o886tshz">Select field...</option>
                  {TARGET_FIELDS.map(field => <option key={field} value={field} data-id="element-11175" data-mp="2ju6sa4ps5hcj">
                      {field}
                    </option>)}
                </select>
                {mapping.status === 'mapped' && !mapping.skipped && <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#10B981]" data-id="element-11176" data-mp="2esr9ykezloxu">
                    <CheckCircle className="w-4 h-4" data-id="element-11177" data-mp="2avv5yevy55pw" />
                  </div>}
              </div>
            </div>

            <div className="flex-1 flex items-center justify-end gap-2" data-id="element-11178" data-mp="20vb2d5kyfp4i">
              <span className="text-xs text-[#64748B]" data-id="element-11179" data-mp="20xfegat0bufv">Skip</span>
              <button onClick={() => {
            const newMappings = [...fieldMappings];
            newMappings[idx].skipped = !newMappings[idx].skipped;
            setFieldMappings(newMappings);
          }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${mapping.skipped ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-11180" data-mp="41zobnmnmkuh">
                <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${mapping.skipped ? 'translate-x-5' : 'translate-x-1'}`} data-id="element-11181" data-mp="1jm0ffehq1qes" />
              </button>
            </div>
          </div>)}
      </Card>

      <div className="bg-[#E0F2FE] border border-[#7DD3FC] rounded-lg p-4" data-id="element-11182" data-mp="2cae9jj5ow2l2">
        <h4 className="text-sm font-semibold text-[#0369A1] mb-1" data-id="element-11183" data-mp="2h9h6shffh4ih">
          Why Join Date matters
        </h4>
        <p className="text-sm text-[#0369A1]" data-id="element-11184" data-mp="22d9o5ljo4b5b">
          Mapping your original join date preserves member tenure. Without it,
          all imported members start as Month 0 in cohort analytics — losing
          their history.
        </p>
      </div>
    </div>;
  const renderStep3 = () => {
    return <div className="max-w-3xl mx-auto space-y-6" data-id="element-11185" data-mp="vwxp7i90up8o">
        <div data-id="element-11186" data-mp="vubej9qu3tm8">
          <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-11187" data-mp="vu1rnv1362kr">
            Preview &amp; configure
          </h2>
          <p className="text-[#64748B] mt-1" data-id="element-11188" data-mp="355cgyds9m98n">
            Review your import settings before running.
          </p>
        </div>

        <Card className="space-y-4" data-id="element-11189" data-mp="3j3tzzy786xk8">
          <Input label="Import name" value={migrationName} onChange={e => setMigrationName(e.target.value)} data-id="element-11190" data-mp="3j2ranupjx36f" />

          <div className="flex items-center gap-2" data-id="element-11191" data-mp="3f1fjvluxybwn">
            <Badge variant="info" data-id="element-11192" data-mp="2reqtebrv9b4f">CSV Upload</Badge>
            <span className="text-sm text-[#64748B]" data-id="element-11193" data-mp="2mel0ks9v1ikr">
              recharge_export_jan2026.csv
            </span>
          </div>

          <p className="text-sm text-[#64748B]" data-id="element-11194" data-mp="3069aqq0jtwz3">Total records found: 847</p>
        </Card>

        <Card data-id="element-11195" data-mp="2ver8lymshqcj">
          <h3 className="font-semibold text-[#0F172A] mb-3" data-id="element-11196" data-mp="2ve82df6i4qec">
            Duplicate Handling
          </h3>
          <div className="space-y-3" data-id="element-11197" data-mp="27odrh35qtbwq">
            {[{
            id: 'skip',
            label: 'Skip duplicates',
            desc: "If an email already exists, don't import that row"
          }, {
            id: 'merge',
            label: 'Merge',
            desc: 'Update existing records with new data, keep existing fields where source is blank'
          }, {
            id: 'overwrite',
            label: 'Overwrite',
            desc: 'Replace existing records entirely with imported data'
          }].map(option => <label key={option.id} className="flex items-start gap-3 cursor-pointer" data-id="element-11198" data-mp="36jykas7fps60">
                <input type="radio" name="duplicateHandling" className="mt-1 text-[#0EA5E9] focus:ring-[#0EA5E9]" checked={duplicateHandling === option.id} onChange={() => setDuplicateHandling(option.id as any)} data-id="element-11199" data-mp="3bh8iaw8w3c5g" />
                <div data-id="element-11200" data-mp="3l8cuxpb3ekx4">
                  <span className="block text-sm font-medium text-[#0F172A]" data-id="element-11201" data-mp="3p957arf9h5xm">
                    {option.label}
                  </span>
                  <span className="block text-xs text-[#64748B]" data-id="element-11202" data-mp="3u9arjmj7bq5o">
                    {option.desc}
                  </span>
                </div>
              </label>)}
          </div>
        </Card>

        <Card data-id="element-11203" data-mp="d655arpm1cuq">
          <label className="flex items-start gap-3 cursor-pointer" data-id="element-11204" data-mp="d6odvhtdeow3">
            <input type="checkbox" checked={testImport} onChange={e => setTestImport(e.target.checked)} className="mt-1 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-11205" data-mp="8692gvsnfrgr" />
            <div data-id="element-11206" data-mp="3vbro1gy71r3d">
              <span className="block text-sm font-medium text-[#0F172A]" data-id="element-11207" data-mp="3vc9t932u3fnf">
                Run test import first (imports first 10 records only)
              </span>
              <span className="block text-xs text-[#64748B] mt-1" data-id="element-11208" data-mp="vy7g3mfklyx2">
                Recommended for your first migration. You can review the test
                results before importing everything.
              </span>
            </div>
          </label>
        </Card>
      </div>;
  };
  const renderStep4 = () => <div className="max-w-3xl mx-auto space-y-6" data-id="element-11209" data-mp="1emuj7ze6se80">
      {isImporting ? <Card className="space-y-6" data-id="element-11210" data-mp="1eo5ns1fghq06">
          <div className="py-8 text-center space-y-6" data-id="element-11211" data-mp="19w5qyhuond7t">
            <div className="relative w-20 h-20 mx-auto" data-id="element-11212" data-mp="19ul135jbciga">
              <div className="absolute inset-0 border-4 border-[#E2E8F0] rounded-full" data-id="element-11213" data-mp="30hhugj0r5bv0"></div>
              <div className="absolute inset-0 border-4 border-[#0EA5E9] rounded-full border-t-transparent animate-spin" data-id="element-11214" data-mp="2wfmw9ne4belg"></div>
              <div className="absolute inset-0 flex items-center justify-center font-bold text-[#0EA5E9]" data-id="element-11215" data-mp="2rhcwezhg7pti">
                {importProgress}%
              </div>
            </div>

            <div data-id="element-11216" data-mp="3k5h41qjkycyu">
              <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-11217" data-mp="3k3xok25r05y4">
                Importing... {Math.round(831 * importProgress / 100)} of 831
                records
              </h3>
              <p className="text-sm text-[#64748B] mt-1" data-id="element-11218" data-mp="3aee3qqyh620m">~3 min remaining</p>
            </div>

            <div className="w-full bg-[#F1F5F9] rounded-full h-2 overflow-hidden max-w-md mx-auto" data-id="element-11219" data-mp="35gus7yt5w3cd">
              <div className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-300" style={{
            width: `${importProgress}%`
          }} data-id="element-11220" data-mp="6qceypz1d2ba" />
            </div>

            <div className="max-w-md mx-auto bg-[#F8FAFC] rounded-lg p-4 text-left space-y-2 h-32 overflow-y-auto border border-[#E2E8F0]" data-id="element-11221" data-mp="9aim16iy58w2">
              {importLogs.map((log, idx) => <div key={idx} className="flex items-center gap-2 text-xs text-[#64748B]" data-id="element-11222" data-mp="e9u0u9zr64b0" data-mpi={idx}>
                  {log.includes('skipped') ? <AlertCircle className="w-3 h-3 text-[#F59E0B]" data-id="element-11223" data-mp="jb190a5t55i7" /> : <Check className="w-3 h-3 text-[#10B981]" data-id="element-11224" data-mp="o8jbpit8eohr" />}
                  {log}
                </div>)}
            </div>
          </div>
        </Card> : <>
          {/* Heading */}
          <div data-id="element-11225" data-mp="x7ockvs9mtx3">
            <h2 className="text-2xl font-bold text-[#0F172A]" data-id="element-11226" data-mp="x750hvpaqzqz">
              Import complete
            </h2>
            <p className="text-[#64748B] mt-1" data-id="element-11227" data-mp="11zocio8c5mto">
              {migrationName || 'Untitled Migration'}
            </p>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-3 gap-4" data-id="element-11228" data-mp="1by5a441s4zop">
            <Card className="text-center py-5" data-id="element-11229" data-mp="1bv9cyhxraf1b">
              <p className="text-xs font-medium text-[#64748B] uppercase tracking-wide mb-1" data-id="element-11230" data-mp="1fy610o62f3cn">
                Imported
              </p>
              <p className="text-2xl font-bold text-[#10B981]" data-id="element-11231" data-mp="3czto9mbb4vs5">831</p>
            </Card>
            <Card className="text-center py-5" data-id="element-11232" data-mp="3821vkq2u95mw">
              <p className="text-xs font-medium text-[#64748B] uppercase tracking-wide mb-1" data-id="element-11233" data-mp="3lrn7xhm4umly">
                Failed
              </p>
              <p className="text-2xl font-bold text-[#EF4444]" data-id="element-11234" data-mp="3h0419ixiomgi">4</p>
            </Card>
            <Card className="text-center py-5" data-id="element-11235" data-mp="3vpa035sg0anv">
              <p className="text-xs font-medium text-[#64748B] uppercase tracking-wide mb-1" data-id="element-11236" data-mp="3vs5xmt0doxbs">
                Skipped
              </p>
              <p className="text-2xl font-bold text-[#94A3B8]" data-id="element-11237" data-mp="8lvj74smfe2l">12</p>
            </Card>
          </div>

          {/* Failed Records Section */}
          <Card padding="none" data-id="element-11238" data-mp="4k9xcty3zpg9">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]" data-id="element-11239" data-mp="2acqg5zykdjaw">
              <h3 className="font-semibold text-[#0F172A]" data-id="element-11240" data-mp="26b4x1wvfs8s9">Failed Records</h3>
              <Button variant="secondary" size="sm" onClick={() => console.log('Download failed records')} data-id="element-11241" data-mp="26d7wt8me5ga6">
                Download failed records (.csv)
              </Button>
            </div>
            <div className="overflow-x-auto" data-id="element-11242" data-mp="2080dfrvc6b5m">
              <table className="w-full text-sm" data-id="element-11243" data-mp="1mhwv3bzkv1lg">
                <thead data-id="element-11244" data-mp="1mh4zi15zd3v6">
                  <tr className="border-b border-[#E2E8F0] bg-[#F8FAFC]" data-id="element-11245" data-mp="1rghw3dmnlpay">
                    <th className="text-left px-5 py-3 text-xs font-medium text-[#64748B] uppercase tracking-[0.5px]" data-id="element-11246" data-mp="1ren6hm0ft90j">
                      #
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-[#64748B] uppercase tracking-[0.5px]" data-id="element-11247" data-mp="1hjd9xdst5tu5">
                      Email
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-[#64748B] uppercase tracking-[0.5px]" data-id="element-11248" data-mp="18h3wog65nmd2">
                      Name
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-[#64748B] uppercase tracking-[0.5px]" data-id="element-11249" data-mp="2xupjqdx2n9xa">
                      Plan
                    </th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-[#64748B] uppercase tracking-[0.5px]" data-id="element-11250" data-mp="12svl5cauy4to">
                      Reason
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-11251" data-mp="sxan1p8wtop7">
                  <tr className="hover:bg-[#F8FAFC] transition-colors" data-id="element-11252" data-mp="oskv2bhdgu9k">
                    <td className="px-5 py-3 text-[#64748B]" data-id="element-11253" data-mp="jv1fn88xubg1">1</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11254" data-mp="jwci94qndd5k">
                      j.martinez@example.com
                    </td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11255" data-mp="a46qnw4b4xqw">Jorge Martinez</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11256" data-mp="55n5vz1g9s04">Premium Club</td>
                    <td className="px-5 py-3" data-id="element-11257" data-mp="53k5cl2o8gh5">
                      <Badge variant="danger" data-id="element-11258" data-mp="14bafcvwack0">Plan not found</Badge>
                    </td>
                  </tr>
                  <tr className="hover:bg-[#F8FAFC] transition-colors" data-id="element-11259" data-mp="3sbe3k9vamswv">
                    <td className="px-5 py-3 text-[#64748B]" data-id="element-11260" data-mp="27stsup93unnd">2</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11261" data-mp="27ueqwoumoopz">—</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11262" data-mp="2cq2amoqccpo3">Lisa Chen</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11263" data-mp="2gsqlt32j28o8">Red Only</td>
                    <td className="px-5 py-3" data-id="element-11264" data-mp="2ls3brwop3vv1">
                      <Badge variant="danger" data-id="element-11265" data-mp="2lqs7zwv92o3x">Missing email</Badge>
                    </td>
                  </tr>
                  <tr className="hover:bg-[#F8FAFC] transition-colors" data-id="element-11266" data-mp="1p3nhxoejz0c4">
                    <td className="px-5 py-3 text-[#64748B]" data-id="element-11267" data-mp="1t4fqefflp17c">3</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11268" data-mp="1y2r2kwtdbnsq">
                      r.patel@wineco.com
                    </td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11269" data-mp="22u92rinkhkcn">Raj Patel</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11270" data-mp="15e3ekth8ikbh">
                      Collector's Tier
                    </td>
                    <td className="px-5 py-3" data-id="element-11271" data-mp="1abv3deml1qbg">
                      <Badge variant="danger" data-id="element-11272" data-mp="1aak0dh0yomle">Plan not found</Badge>
                    </td>
                  </tr>
                  <tr className="hover:bg-[#F8FAFC] transition-colors" data-id="element-11273" data-mp="28oqzv5206f1j">
                    <td className="px-5 py-3 text-[#64748B]" data-id="element-11274" data-mp="28mmvyn43tg19">4</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11275" data-mp="2meucr5ezsweb">—</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11276" data-mp="2ieshpf3rj180">Amy Nguyen</td>
                    <td className="px-5 py-3 text-[#0F172A]" data-id="element-11277" data-mp="2igwp470veid1">Reserve Club</td>
                    <td className="px-5 py-3" data-id="element-11278" data-mp="1up7zwzjcia0b">
                      <Badge variant="danger" data-id="element-11279" data-mp="1pqekh1ki08xi">Missing email</Badge>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>
        </>}
    </div>;
  return <div className="bg-[#F1F5F9] min-h-full flex flex-col" data-id="element-11280" data-mp="1yq9z5gd9im2a">
      {/* Top Bar */}
      <div className="bg-white border-b border-[#E2E8F0] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.05)]" data-id="element-11281" data-mp="3f49yoq8749p0">
        {renderStepper()}
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 pb-24" data-id="element-11282" data-mp="3a82pmoki6i2u">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
      </div>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 md:left-[240px] bg-white border-t border-[#E2E8F0] px-6 py-4 flex items-center justify-between z-10" data-id="element-11283" data-mp="38pumf9et8x7y">
        <div className="flex items-center gap-3" data-id="element-11284" data-mp="582pdglthn2w">
          {currentStep === 4 && !isImporting && <Button variant="ghost" onClick={() => console.log('Re-import failed records')} data-id="element-11285" data-mp="a6xibbdi0s0q">
              Re-import failed records
            </Button>}
          <Button variant="ghost" onClick={() => navigate('/management/migration')} data-id="element-11286" data-mp="3rmm5bhqfxabh">
            Cancel
          </Button>
        </div>

        <div className="flex items-center gap-3" data-id="element-11287" data-mp="erf15hsqo68">
          {currentStep > 1 && !isImporting && !(currentStep === 4) && <Button variant="secondary" onClick={handleBack} data-id="element-11288" data-mp="sxexse9blr45">
              Back
            </Button>}

          {!isImporting && !(currentStep === 4) && <Button variant="secondary" onClick={() => console.log('Save Draft')} data-id="element-11289" data-mp="2p1l7ojfrzw5x">
              Save Draft
            </Button>}

          {!isImporting && <Button variant="primary" onClick={currentStep === 4 ? handleDone : currentStep === 3 ? () => {
          handleNext();
          handleStartImport();
        } : handleNext} disabled={currentStep === 1 && (!selectedPlatform || !uploadedFile)} iconRight={currentStep === 4 ? undefined : <ChevronRight className="w-4 h-4" data-id="element-11291" data-mp="37xbdkhqtxcp3" />} data-id="element-11290" data-mp="2k807ckqpmq3k">
              {currentStep === 4 ? 'Done' : currentStep === 3 ? 'Start Import' : 'Next Step'}
            </Button>}
        </div>
      </div>

      {/* Polling Overlay */}
      {isPolling && <div className="fixed inset-0 flex items-center justify-center p-4" style={{
      backgroundColor: 'rgba(15, 23, 42, 0.5)',
      zIndex: 9999
    }} data-id="element-11292" data-mp="2tzwhwv9zg8oo">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[480px]" data-id="element-11293" data-mp="1nkvjmixhjeex">
            <div className="p-8 py-12 text-center" data-id="element-11294" data-mp="1iiwhfcdhenjk">
              <div className="flex justify-center mb-6" data-id="element-11295" data-mp="3u831s5ggkzvx">
                <div className="relative w-14 h-14" data-id="element-11296" data-mp="3u9nvjsuy8esq">
                  <div className="absolute inset-0 border-[3px] border-[#E0F2FE] rounded-full" data-id="element-11297" data-mp="3105x0poe5fd"></div>
                  <div className="absolute inset-0 border-[3px] border-[#0EA5E9] rounded-full border-t-transparent animate-spin" data-id="element-11298" data-mp="72v8s9n98qv9"></div>
                </div>
              </div>
              <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-11299" data-mp="gswwv1fly9a6">
                Verifying eligibility
              </h2>
              <p className="text-[14px] text-[#64748B] leading-relaxed" data-id="element-11300" data-mp="lqyfjl94g6l4">
                We are confirming your store is eligible for legacy gateway
                access.
              </p>
              <p className="text-[13px] text-[#94A3B8] mt-4" data-id="element-11301" data-mp="uszbmesmcml7">
                This usually takes a few seconds. Please don't close this
                window.
              </p>
            </div>
          </div>
        </div>}
    </div>;
}